import React from 'react';
import { UncontrolledCollapse, Card, CardBody, Col, Row } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const UnitTimeline = ({ unit }) => {
    const navigate = useNavigate();
    const formatDate = (date) => moment(date).format('DD MMM YYYY');

    const calculateProfit = () => {
        if (!unit.settlement_price_obj) return null;

        const profit = unit.settlement_price_obj.settled_price - unit.initial_price;
        const profitPercentage = (profit / unit.initial_price) * 100;

        return {
            profit,
            profitPercentage
        };
    };

    // Helper function to create navigation buttons as divs
    const NavButton = ({ onClick, icon, text, className = "" }) => (
        <div
            className={`btn btn-soft-secondary btn-sm d-flex align-items-center mx-5 ${className}`}
            onClick={onClick}
            role="button"
            style={{ cursor: 'pointer' }}
        >
            <i className={`${icon} me-1`}></i>
            <span className="text-muted">{text}</span>
        </div>
    );

    const timelineItems = [];

    // Sale Information (Most Recent) - Only if contract exists
    if (unit.contract_obj) {
        timelineItems.push(
            <div className="accordion-item border-0" key="sale">
                <div className="accordion-header">
                    <button className="accordion-button p-2 shadow-none" id="headingSale">
                        <div className="d-flex">
                            <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title bg-light text-warning rounded-circle shadow">
                                    <i className="ri-shopping-cart-line"></i>
                                </div>
                            </div>
                            <div className="flex-grow-1 ms-3">
                                <h6 className="fs-14 mb-1 text-success fw-bold">Unit Sold</h6>
                                <small className="text-muted">
                                    {formatDate(unit.contract_obj.pvc_date)}
                                </small>
                            </div>
                        </div>
                    </button>
                </div>
                <UncontrolledCollapse toggler="#headingSale" defaultOpen>
                    <div className="accordion-body ms-2 ps-5">
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-semibold text-dark">Contract Details:</span>
                                {unit.client_obj && (
                                    <NavButton
                                        onClick={() => navigate(`/contracts/${unit.contract}`)}
                                        icon="ri-file-list-3-line"
                                        text="View Contract"
                                    />
                                )}
                            </div>
                            <div className="ms-2 mt-2">
                                <div className="text-primary fw-semibold">#{unit.contract_obj.pvc_number}</div>
                                <div>Type: <span className="text-success">{unit.contract_obj.type}</span></div>
                                <div>Notary: {unit.contract_obj.notary}</div>
                            </div>
                        </div>

                        {unit.client_obj && (
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-semibold text-dark">Client:</span>
                                    <NavButton
                                        onClick={() => navigate(`/client-details/${unit.client}`)}
                                        icon="ri-user-3-line"
                                        text="View Client"
                                    />
                                </div>
                                <div className="ms-2 mt-2">
                                    <div className="text-primary fw-semibold">
                                        {unit.client_obj.name} {unit.client_obj.surname || ''}
                                    </div>
                                    {unit.client_obj.buyer_type && (
                                        <div className="badge bg-soft-info text-info">
                                            {unit.client_obj.buyer_type}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {unit.settlement_price_obj && (
                            <div className="mb-3">
                                <span className="fw-semibold text-dark">Settlement Price:</span>
                                <div className="ms-2 mt-2">
                                    <span className="text-success fw-bold">
                                        €{Intl.NumberFormat('en-US').format(unit.settlement_price_obj.settled_price)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </UncontrolledCollapse>
            </div>
        );
    }

    // Location Details (Middle)
    timelineItems.push(
        <div className="accordion-item border-0" key="location">
            <div className="accordion-header">
                <button className="accordion-button p-2 shadow-none" id="headingLocation">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                            <div className="flex-shrink-0 avatar-xs">
                                <div className="avatar-title bg-light text-primary rounded-circle shadow">
                                    <i className="ri-map-pin-line"></i>
                                </div>
                            </div>
                            <div className="flex-grow-1 ms-3">
                                <h6 className="fs-14 mb-1 fw-bold">Location Details</h6>
                                <small className="text-primary fw-semibold">
                                    {unit.block_obj.name} • {unit.phase} • {unit.project}
                                </small>
                            </div>
                        </div>
                        <NavButton
                            onClick={() => navigate(`/block-details/${unit.block}`)}
                            icon="ri-building-2-line"
                            text="View Block"
                        />
                    </div>
                </button>
            </div>
            <UncontrolledCollapse toggler="#headingLocation" defaultOpen>
                <div className="accordion-body ms-2 ps-5">
                    <div className="card bg-soft-light border-0">
                        <div className="card-body py-2">
                            <div className="text-dark fw-semibold mb-2">Block Information:</div>
                            <div className="row">
                                <div className="col-6">
                                    <div className="mb-1">Total Floors: <span className="text-primary">{unit.block_obj.total_floors}</span></div>
                                    <div>Total Units: <span className="text-primary">{unit.block_obj.total_flats}</span></div>
                                </div>
                                <div className="col-6">
                                    <div className="mb-1">Floor: <span className="text-primary">{unit.floor}</span></div>
                                    <div>Unit: <span className="text-primary">{unit.nr}</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </UncontrolledCollapse>
        </div>
    );

    // Rest of the component remains the same...

    return (
        <div className="row">
            <div className="col-md-8">
                <div className="profile-timeline">
                    <div className="accordion accordion-flush" id="unitTimeline">
                        {timelineItems}
                    </div>
                </div>
            </div>
            <div className="col-md-4 d-flex justify-content-center">
                {unit.contract_obj && (
                    <Card className="card-animate w-100" style={{ maxHeight: '200px', border: '2px solid #e0e0e0', boxShadow: '0 4px 8px rgba(0,0,0,0.05)' }}>
                        <CardBody>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <p className="fw-medium text-muted mb-0">Profit Margin</p>
                                    <h2 className="mt-4 ff-secondary fw-semibold">
                                        €{Intl.NumberFormat('en-US').format(calculateProfit().profit)}
                                    </h2>
                                    <p className="mb-0 text-muted">
                                        <span className={`badge bg-light ${calculateProfit().profit >= 0 ? 'text-success' : 'text-danger'} mb-0`}>
                                            <i className={`ri-arrow-${calculateProfit().profit >= 0 ? 'up' : 'down'}-line align-middle`}></i>
                                            {' '}{calculateProfit().profitPercentage.toFixed(2)}%
                                        </span>
                                        {' '}profit margin
                                    </p>
                                </div>
                                <div>
                                    <div className="avatar-sm flex-shrink-0">
                                        <span className={`avatar-title ${calculateProfit().profit >= 0 ? 'bg-success' : 'bg-danger'} rounded-circle fs-2`}>
                                            <i className={`ri-${calculateProfit().profit >= 0 ? 'money-dollar-circle-line' : 'money-dollar-circle-fill'}`}></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 pt-2 border-top">
                                <div className="d-flex mb-2">
                                    <div className="flex-grow-1">
                                        <p className="text-muted mb-0">Initial Price:</p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <h6 className="mb-0">€{Intl.NumberFormat('en-US').format(unit.initial_price)}</h6>
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <div className="flex-grow-1">
                                        <p className="text-muted mb-0">Settlement Price:</p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <h6 className="mb-0">€{Intl.NumberFormat('en-US').format(unit.settlement_price_obj.settled_price)}</h6>
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default UnitTimeline;
